import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';

import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCardModule } from '@angular/material/card';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';

import { ApiService } from '../../core/services/api.service';
import { ProjectService } from '../projects/project.service';
import { ProjectDto } from '../projects/project.models';

interface TeamLeadOption {
  leadEmployeeId: string;
  leadName: string;
  teamName: string;
  kind: number;
}

interface ProjectTaskRow {
  id: string;
  title: string;
  status: number;
  statusLabel?: string;
  priority: number;
  dueDate?: string;
  isOverdue?: boolean;
}

@Component({
  selector: 'app-project-tasks',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    MatButtonModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatCardModule,
    MatTableModule,
    MatTooltipModule
  ],
  templateUrl: './project-tasks.component.html',
  styleUrl: './project-tasks.component.scss'
})
export class ProjectTasksComponent implements OnInit, OnDestroy {
  private readonly api = inject(ApiService);
  private readonly projectService = inject(ProjectService);
  private readonly router = inject(Router);
  private readonly destroy$ = new Subject<void>();

  readonly loading = signal(false);
  readonly loadingTasks = signal(false);
  readonly creating = signal(false);
  readonly error = signal<string | null>(null);
  readonly projects = signal<ProjectDto[]>([]);
  readonly tasks = signal<ProjectTaskRow[]>([]);
  readonly teamLeads = signal<TeamLeadOption[]>([]);

  selectedProjectId = '';
  showCreate = true;

  newTitle = '';
  newDescription = '';
  newTeamLeadId = '';
  newTeamKind: number | '' = '';
  newPriority = 1;
  newStartDate = '';
  newDueDate = '';

  readonly columns = ['title', 'status', 'priority', 'deadline'];
  readonly priorityOptions = [
    { value: 0, label: 'Low' },
    { value: 1, label: 'Medium' },
    { value: 2, label: 'High' },
    { value: 3, label: 'Critical' }
  ];
  readonly teamKindOptions = [
    { value: 0, label: 'Developer' },
    { value: 1, label: 'QA' },
    { value: 2, label: 'Designer' },
    { value: 3, label: 'ASO' }
  ];

  ngOnInit(): void {
    this.loadProjects();
    this.loadTeamLeads();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  filteredLeads(): TeamLeadOption[] {
    if (this.newTeamKind === '') return this.teamLeads();
    return this.teamLeads().filter((l) => l.kind === this.newTeamKind);
  }

  onProjectChange(): void {
    this.loadTasks();
  }

  loadProjects(): void {
    this.loading.set(true);
    this.projectService.list({ page: 1, pageSize: 100, activeOnly: true }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (page) => {
        const items = page.items ?? [];
        this.projects.set(items);
        this.loading.set(false);
        if (!this.selectedProjectId && items.length) {
          this.selectedProjectId = items[0].id;
          this.loadTasks();
        }
      },
      error: (err) => {
        this.error.set(err?.message || 'Failed to load projects.');
        this.loading.set(false);
      }
    });
  }

  loadTeamLeads(): void {
    this.api.get<unknown>('v1/tasks/allocation/leads').pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        const raw = Array.isArray(res) ? res : ((res as { items?: unknown[] })?.items ?? []);
        this.teamLeads.set((raw as Record<string, unknown>[]).map((x) => ({
          leadEmployeeId: String(x['leadEmployeeId'] ?? x['LeadEmployeeId']),
          leadName: String(x['leadName'] ?? x['LeadName'] ?? 'Team lead'),
          teamName: String(x['teamName'] ?? x['TeamName'] ?? ''),
          kind: Number(x['kind'] ?? x['Kind'] ?? 0)
        })));
      },
      error: () => this.teamLeads.set([])
    });
  }

  loadTasks(): void {
    if (!this.selectedProjectId) {
      this.tasks.set([]);
      return;
    }
    this.loadingTasks.set(true);
    this.api.getPaged<ProjectTaskRow>('v1/tasks', {
      page: 1,
      pageSize: 100,
      projectId: this.selectedProjectId
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (page) => {
        this.tasks.set(page.items ?? []);
        this.loadingTasks.set(false);
      },
      error: (err) => {
        this.error.set(err?.message || 'Failed to load tasks.');
        this.loadingTasks.set(false);
      }
    });
  }

  createTask(): void {
    if (!this.selectedProjectId) {
      this.error.set('Select a project first.');
      return;
    }
    if (!this.newTitle.trim()) {
      this.error.set('Task title is required.');
      return;
    }
    if (!this.newDueDate) {
      this.error.set('Deadline is required.');
      return;
    }
    if (!this.newTeamLeadId) {
      this.error.set('Team lead is required.');
      return;
    }
    if (this.newStartDate && this.newDueDate < this.newStartDate) {
      this.error.set('Deadline cannot be earlier than start date.');
      return;
    }

    this.creating.set(true);
    this.error.set(null);
    const body: Record<string, unknown> = {
      projectId: this.selectedProjectId,
      title: this.newTitle.trim(),
      description: this.newDescription.trim() || undefined,
      teamLeadId: this.newTeamLeadId,
      priority: this.newPriority,
      dueDate: this.newDueDate,
      startDate: this.newStartDate || undefined
    };
    if (this.newTeamKind !== '') body['teamKind'] = this.newTeamKind;

    this.api.post('v1/tasks', body).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.creating.set(false);
        this.newTitle = '';
        this.newDescription = '';
        this.newTeamLeadId = '';
        this.newTeamKind = '';
        this.newPriority = 1;
        this.newStartDate = '';
        this.newDueDate = '';
        this.loadTasks();
      },
      error: (err) => {
        this.error.set(err?.message || 'Failed to create task.');
        this.creating.set(false);
      }
    });
  }

  openProject(): void {
    if (!this.selectedProjectId) return;
    void this.router.navigate(['/projects', this.selectedProjectId], {
      queryParams: { tab: 'tasks' }
    });
  }

  priorityLabel(value: number): string {
    return this.priorityOptions.find((p) => p.value === value)?.label ?? String(value);
  }
}
